import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Tag, ArrowRight, Store, Layers, Info } from "lucide-react";
import api from "../lib/apiClient";
import { AFFILIATE_DISCLOSURE } from "../lib/siteConfig";
import { ImageStage } from "../components/ImageStage";

export default function LatestDeals() {
  const [deals, setDeals] = useState(null);

  useEffect(() => {
    api.get("/public-deals").then(({ data }) => setDeals(data)).catch(() => setDeals([]));
  }, []);

  return (
    <div className="space-y-6 fade-up">
      <div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl text-ink flex items-center gap-2">
          <Tag className="text-brand" /> Latest Deals
        </h1>
        <p className="text-ink-soft mt-1" data-testid="deals-subtitle">Coordinated family sets — no account needed. Nothing is for sale on Siblingo yet; retailer prices and stock will be shown here once our first collections arrive.</p>
      </div>

      {/* Top-of-page affiliate disclosure */}
      <div data-testid="deals-disclosure-banner" className="flex items-start gap-2 bg-blush border border-brand/15 rounded-2xl p-4 text-sm text-ink-soft">
        <Info size={16} className="text-brand shrink-0 mt-0.5" />
        <span>{AFFILIATE_DISCLOSURE}</span>
      </div>

      {deals === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-3xl border border-line h-80 animate-pulse" />
          ))}
        </div>
      ) : deals.length === 0 ? (
        <div data-testid="deals-empty" className="bg-white rounded-3xl border border-line p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-blush text-brand flex items-center justify-center mx-auto mb-4"><Tag size={26} /></div>
          <p className="font-heading font-extrabold text-xl text-ink">Our first collections are on the way</p>
          <p className="text-ink-soft mt-1">Nothing is listed yet. Explore a sample look, or set up your family's sizes so you're ready when collections arrive.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-2"><Link to="/demo-collection" data-testid="deals-empty-demo" className="h-11 px-5 inline-flex items-center rounded-full bg-brand text-white font-bold text-sm hover:bg-brand-hover transition-all">Explore a sample look</Link><Link to="/family-profile" data-testid="deals-empty-sizing" className="h-11 px-5 inline-flex items-center rounded-full border-2 border-brand/40 bg-white text-brand font-bold text-sm hover:bg-blush/50 transition-all">Set up your family's sizes</Link></div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="deals-grid">
          {deals.map((d) => <DealTile key={d.id} deal={d} />)}
        </div>
      )}
    </div>
  );
}

function DealTile({ deal }) {
  const navigate = useNavigate();
  const image = deal.image || (deal.collage && deal.collage[0] && deal.collage[0].image) || (deal.items && deal.items[0] && deal.items[0].image);
  const price = deal.family_total != null ? deal.family_total : deal.total_price;
  const matchLabel = deal.complete_the_look ? "Complete the Look" : (deal.match_type === "exact_match" ? "Exact Match" : "Coordinating");
  const memberCount = (deal.items || []).length || (deal.item_ids || []).length;
  return (
    <a
      href={`/deal/${deal.id}`}
      onClick={(e) => { e.preventDefault(); navigate(`/deal/${deal.id}`); }}
      data-testid={`public-deal-${deal.id}`}
      aria-label={`${deal.name} — ${matchLabel} family set on SIBLINGO`}
      className="group bg-white rounded-3xl border border-line shadow-soft overflow-hidden hover:shadow-lift hover:-translate-y-1 transition-all duration-300 flex flex-col no-underline"
    >
      <ImageStage src={image} alt={`${deal.name} — product image`} ratio="landscape" pad="p-3" testid={`public-deal-image-${deal.id}`}>
        <span className="absolute top-3 left-3 bg-brand text-white text-xs font-extrabold px-3 py-1.5 rounded-full shadow">{matchLabel}</span>
        {deal.occasion && (
          <span className="absolute top-3 right-3 bg-white/90 backdrop-blur text-ink text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 capitalize">
            <Layers size={12} /> {String(deal.occasion).replace(/_/g, " ")}
          </span>
        )}
      </ImageStage>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-xs font-bold uppercase tracking-wider text-brand flex items-center gap-1"><Store size={12} /> {deal.main_source || (deal.retailers && deal.retailers[0]) || "Leveret"}</p>
        <h3 className="font-heading font-extrabold text-lg text-ink leading-snug mt-1">{deal.name}</h3>
        {price != null && price > 0 && (
          <div className="flex items-baseline gap-2 mt-2">
            <span data-testid={`public-deal-price-${deal.id}`} className="font-heading font-black text-2xl text-ink">${Number(price).toFixed(2)}</span>
            <span className="text-xs text-ink-muted">complete set{memberCount ? ` · ${memberCount} pieces` : ""}</span>
          </div>
        )}
        <div className="mt-4 flex items-center gap-2">
          <span
            data-testid={`public-deal-link-${deal.id}`}
            className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-full bg-brand text-white font-bold text-sm group-hover:bg-brand-hover transition-all"
          >
            View set <ArrowRight size={16} />
          </span>
        </div>
        <p className="text-[11px] text-ink-muted mt-2 leading-snug">{AFFILIATE_DISCLOSURE}</p>
      </div>
    </a>
  );
}
